import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

export function useUserManagement() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch pending users
  const fetchPendingUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const getPendingUsersFn = httpsCallable(functions, 'getPendingUsers');
      const result = await getPendingUsersFn();
      setPendingUsers(result.data.users || []);
    } catch (err) {
      const message = err.message || 'Failed to fetch pending users';
      setError(message);
      console.error('Error fetching pending users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all users
  const fetchAllUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const getAllUsersFn = httpsCallable(functions, 'getAllUsers');
      const result = await getAllUsersFn();
      setAllUsers(result.data.users || []);
    } catch (err) {
      const message = err.message || 'Failed to fetch all users';
      setError(message);
      console.error('Error fetching all users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  // Approve a user
  const approveUser = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const approveUserFn = httpsCallable(functions, 'approveUser');
      await approveUserFn({ userId });
      // Refresh the list
      await fetchPendingUsers();
      return { success: true };
    } catch (err) {
      const message = err.message || 'Failed to approve user';
      setError(message);
      console.error('Error approving user:', err);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchPendingUsers]);

  // Reject a user
  const rejectUser = useCallback(async (userId, reason) => {
    setLoading(true);
    setError(null);
    try {
      const rejectUserFn = httpsCallable(functions, 'rejectUser');
      await rejectUserFn({ userId, reason });
      // Refresh the list
      await fetchPendingUsers();
      return { success: true };
    } catch (err) {
      const message = err.message || 'Failed to reject user';
      setError(message);
      console.error('Error rejecting user:', err);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchPendingUsers]);

  // Promote user to admin
  const promoteToAdmin = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const promoteToAdminFn = httpsCallable(functions, 'promoteToAdmin');
      await promoteToAdminFn({ userId });
      // Refresh both lists
      await Promise.all([fetchPendingUsers(), fetchAllUsers()]);
      return { success: true };
    } catch (err) {
      const message = err.message || 'Failed to promote user';
      setError(message);
      console.error('Error promoting user:', err);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchPendingUsers, fetchAllUsers]);

  return {
    pendingUsers,
    allUsers,
    loading,
    error,
    approveUser,
    rejectUser,
    promoteToAdmin,
    refreshUsers: fetchPendingUsers,
    refreshAllUsers: fetchAllUsers,
  };
}
